import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ListGroup, Popover, PopoverBody, Badge } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFetch, ListGroupItem } from '@cordata/buffet';

import { UncontrolledTable, PseudoStates } from '~/components';
import PatientsBannerModal from '~/components/Patient/Identity/Banner/Modal';

import { authorizedPyPublicInstance as Py } from '~/utils';

const SiblingsInCustodyCell = (data) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState('loading');
  const [siblings, setSiblings] = useState();
  const [isSiblingModalOpen, setIsSiblingModalOpen] = useState(false);
  const [siblingForModal, setSiblingForModal] = useState();

  const toggleIsOpen = () =>
    (data.original.attributes.siblingsInCustody > 0 && data.original.attributes.caseCategory !== 'ADOPT'
      ? setIsOpen(p => !p)
      : setIsOpen(false));

  useEffect(() => {
    setStatus('loading');

    if (isOpen) {
      Py
        .get('list-view-cs-tbl-patient-sibling', {
          params: {
            case_id: data.original.attributes.caseId,
            'patient_uid__!': data.original.attributes.patientUid,
          },
        })
        .then(r => r.data.data)
        .then(r => {
          setSiblings(r);
          setStatus('success');
        })
        .catch(() => setStatus('error'));
    }
  }, [isOpen]);

  const toggleSiblingModal = () => {
    setSiblingForModal(undefined);
    setIsSiblingModalOpen(!isSiblingModalOpen);
  };

  useEffect(() => {
    setIsSiblingModalOpen(!!siblingForModal);
  }, [siblingForModal]);

  return (
    <>
      <Badge
        id={`s-${data.original.id}`}
        color="secondary"
        pill
        className="cursor-pointer"
        onClick={toggleIsOpen}
      >
        {data.original.attributes.siblingsInCustody > 0 && (
          <FontAwesomeIcon icon="chevron-right" rotation={isOpen ? '90' : null} fixedWidth className="mr-3" />
        )}

        {data.original.attributes.siblingsInCustody}
      </Badge>
      {!isSiblingModalOpen &&
        <Popover
          placement="bottom"
          target={`s-${data.original.id}`}
          isOpen={isOpen}
          toggle={toggleIsOpen}
          hideArrow
        >
          <PopoverBody>
            {status === 'success'
              ? (
                <ListGroup flush>
                  {siblings.map(sib => (
                    <ListGroupItem
                      key={sib.id}
                      action
                      tag="button"
                      onClick={() => setSiblingForModal(sib)}
                    >
                      {sib.attributes.lastName}, {sib.attributes.firstName}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              ) : <PseudoStates mini status={status} />
            }
          </PopoverBody>
        </Popover>
      }
      <PatientsBannerModal
        isOpen={isSiblingModalOpen}
        toggleIsOpen={toggleSiblingModal}
        patient={siblingForModal}
      />
    </>
  );
};

const PlacementCountCell = (data) => {
  const [, items] = useFetch(
    'data/public/list-view-cs-tbl-source-placement-history',
    {
      params: {
        patient_uid: data.original.attributes.patientUid,
      },
      initialState: [],
      instance: 'p',
    },
  );

  return items ? items.length : 0;
};

const Case = () => {
  const params = useParams();

  return (
    <UncontrolledTable
      name="case"
      className="mx-4"
      endpoint={{
        url: 'data/public/list-view-cs-tbl-source-case',
        params: {
          patient_uid: params.patient_id,
          distinct: 'case_id,open_date_dt',
        },
      }}
      defaultSorted={[{ id: 'openDateDt', desc: true }]}
      columns={[
        {
          id: 'county',
          Header: 'County',
          accessor: 'attributes.county',
        },
        {
          id: 'caseName',
          Header: 'Case Name',
          accessor: 'attributes.caseName',
        },
        {
          id: 'openDateDt',
          Header: 'Case Open Date',
          accessor: 'attributes.openDateDt',
          cellType: 'date',
        },
        {
          id: 'siblingsInCustody',
          Header: 'Siblings in Custody',
          headerTooltip: 'The number of other children in agency custody who are active case members of the same case.',
          accessor: 'attributes.siblingsInCustody',
          Cell: SiblingsInCustodyCell,
          cellType: 'number',
        },
        {
          id: 'placementCount',
          Header: 'Placement Count',
          accessor: 'attributes.placementCount',
          Cell: PlacementCountCell,
          cellType: 'number',
        },
        {
          id: 'casePlanGoal',
          Header: 'Case Plan Goal Summary',
          accessor: 'attributes.casePlanGoal',
        },
        {
          id: 'lastSarDateDt',
          Header: 'Last Semi-annual Review Date',
          accessor: 'attributes.lastSarDateDt',
          cellType: {
            type: 'date',
          },
        },
      ]}
    />
  );
};

export default Case;
